import { useMutation } from '@tanstack/react-query';

import API from '@api/API';

import { BatchReconciliationAction } from '@admin/queries/AdminBatchReconciliationActionsQueries';

export const useRejectBatchReconciliationActionMutation = () => {
  const { isPending, mutateAsync } = useMutation({
    mutationFn: ({ actionId, reason }: { actionId: string; reason: string }) =>
      API.post<{ batchReconciliationAction: BatchReconciliationAction }, { reason: string }>(
        `/v1/super-admin/batch-reconciliation-actions/${actionId}/reject`,
        { reason },
      ),
  });

  return {
    isRejectingBatchReconciliationAction: isPending,
    rejectBatchReconciliationActionAsync: mutateAsync,
  };
};

export const useRemoveBatchReconciliationActionDuplicateMutation = () => {
  const { isPending, mutateAsync } = useMutation({
    mutationFn: ({
      actionId,
      eventId,
      reason,
    }: {
      actionId: string;
      eventId: string;
      reason: string;
    }) =>
      API.post<
        { batchReconciliationAction: BatchReconciliationAction },
        { eventId: string; reason: string }
      >(`/v1/super-admin/batch-reconciliation-actions/${actionId}/remove-duplicate`, {
        eventId,
        reason,
      }),
  });

  return {
    isRemovingBatchReconciliationActionDuplicate: isPending,
    removeBatchReconciliationActionDuplicateAsync: mutateAsync,
  };
};

export const useMergeBatchReconciliationActionIntoMutation = () => {
  const { isPending, mutateAsync } = useMutation({
    mutationFn: ({
      actionId,
      targetActionId,
      reason,
    }: {
      actionId: string;
      targetActionId: string;
      reason: string;
    }) =>
      API.post<
        { source: BatchReconciliationAction; target: BatchReconciliationAction },
        { targetActionId: string; reason: string }
      >(`/v1/super-admin/batch-reconciliation-actions/${actionId}/merge-into`, {
        targetActionId,
        reason,
      }),
  });

  return {
    isMergingBatchReconciliationActionInto: isPending,
    mergeBatchReconciliationActionIntoAsync: mutateAsync,
  };
};

interface FinalizeBatchReconciliationActionPayload {
  approvedEventId?: string;
  titleFromEventId?: string;
  imageFromEventId: string;
  venueName: string;
  claimedEntertainerIds: string[];
  cityId: string;
  seriesIds: string[];
}

export const useFinalizeBatchReconciliationActionMutation = () => {
  const { isPending, mutateAsync } = useMutation({
    mutationFn: ({
      actionId,
      ...payload
    }: { actionId: string } & FinalizeBatchReconciliationActionPayload) =>
      API.post<
        { batchReconciliationAction: BatchReconciliationAction },
        FinalizeBatchReconciliationActionPayload
      >(`/v1/super-admin/batch-reconciliation-actions/${actionId}/finalize`, payload),
  });

  return {
    isFinalizingBatchReconciliationAction: isPending,
    finalizeBatchReconciliationActionAsync: mutateAsync,
  };
};
